// VendorList.jsx
// Vendor management list page

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import VendorAPI from '../../../services/vendorService';
import AddVendorModal from './AddVendorModal';

const DESKTOP_BREAKPOINT = 1024;

class VendorList extends Component {
    state = {
        vendors: [],
        searchTerm: '',
        showAddModal: false,
        loading: true,
        error: null,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadVendors();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadVendors = async () => {
        try {
            const raw = await VendorAPI.getVendors(this.props.typeId);
            // Normalize: server may return `id` instead of `vendor_id`
            const vendors = (Array.isArray(raw) ? raw : []).map(v => ({
                ...v,
                vendor_id: v.vendor_id || v.id
            }));
            this.setState({ vendors, loading: false });
        } catch (err) {
            console.error('Error loading vendors:', err);
            this.setState({ error: 'Failed to load vendors.', loading: false });
        }
    };

    handleDeactivate = async (vendor) => {
        if (!window.confirm(`Deactivate ${vendor.vendor_name}? They won't appear in dropdowns.`)) return;
        try {
            await VendorAPI.deleteVendor(vendor.vendor_id);
            await this.loadVendors();
        } catch (err) {
            console.error('Error deactivating vendor:', err);
        }
    };

    handleReactivate = async (vendor) => {
        try {
            // Re-activate by patching is_active back to true
            await VendorAPI.updateVendor(vendor.vendor_id, { is_active: true });
            await this.loadVendors();
        } catch (err) {
            console.error('Error reactivating vendor:', err);
        }
    };

    getFilteredVendors = () => {
        const { vendors, searchTerm } = this.state;
        if (!searchTerm.trim()) return vendors;
        const term = searchTerm.toLowerCase();
        return vendors.filter(v =>
            (v.vendor_name || '').toLowerCase().includes(term) ||
            (v.contact_name || '').toLowerCase().includes(term) ||
            (v.contact_email || '').toLowerCase().includes(term)
        );
    };

    render() {
        const { loading, error, searchTerm, showAddModal, isDesktop } = this.state;
        const filtered = this.getFilteredVendors();
        const embedded = this.props.embedded;

        return (
            <div className={`vendor-container ${!isDesktop ? 'mobile' : ''} ${embedded ? 'embedded' : ''}`}>
                <style>{`
                    .vendor-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: var(--space-6);
                        background: var(--color-bg-panel);
                        min-height: 100vh;
                    }
                    .vendor-container.embedded {
                        padding: 0;
                        min-height: auto;
                        background: transparent;
                    }
                    .vendor-container.mobile { padding: var(--space-4); }
                    .vendor-container.mobile.embedded { padding: 0; }
                `}</style>

                <div className="jv-page-header">
                    <h2 className="jv-page-title"><i className="fa-sharp fa-regular fa-truck-field mr-2"></i>Vendors</h2>
                    <div className="jv-page-actions">
                        <button className="jv-btn jv-btn-primary" onClick={() => this.setState({ showAddModal: true })}>
                            <i className="fa-sharp fa-regular fa-plus"></i> Add Vendor
                        </button>
                    </div>
                </div>

                <div className="jv-card">
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <input
                            className="jv-input"
                            type="text"
                            placeholder="Search vendors..."
                            value={searchTerm}
                            onChange={(e) => this.setState({ searchTerm: e.target.value })}
                            style={{ minWidth: '240px' }}
                        />
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                            <p className="mt-3">Loading vendors...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : filtered.length === 0 ? (
                        <div className="jv-empty-state">
                            <i className="fa-sharp fa-regular fa-truck-field"></i>
                            <p>{searchTerm ? 'No vendors match your search.' : 'No vendors yet. Add your first vendor to get started.'}</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="jv-table">
                                <thead>
                                    <tr>
                                        <th>Vendor</th>
                                        <th>Contact</th>
                                        {isDesktop && <th>Email</th>}
                                        {isDesktop && <th>Phone</th>}
                                        <th>Lead Time</th>
                                        <th>Products</th>
                                        <th>Status</th>
                                        <th style={{ width: '120px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(vendor => (
                                        <tr key={vendor.vendor_id}>
                                            <td>
                                                <Link to={`/vendors/${vendor.vendor_id}`} style={{ color: 'var(--color-brand-teal)', textDecoration: 'none', fontWeight: 'var(--font-weight-medium)' }}>
                                                    {vendor.vendor_name}
                                                </Link>
                                            </td>
                                            <td>{vendor.contact_name || '—'}</td>
                                            {isDesktop && <td>{vendor.contact_email || '—'}</td>}
                                            {isDesktop && <td>{vendor.contact_phone || '—'}</td>}
                                            <td>{vendor.lead_time_days ? `${vendor.lead_time_days}d` : '—'}</td>
                                            <td>{vendor.product_count || 0}</td>
                                            <td>
                                                <span className={`jv-badge ${vendor.is_active !== false ? 'jv-badge-success' : 'jv-badge-danger'}`}>
                                                    {vendor.is_active !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="jv-btn-icon" title="Edit" onClick={() => this.props.history ? this.props.history.push(`/vendors/${vendor.vendor_id}`) : null}>
                                                    <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                                                </button>
                                                {vendor.is_active !== false ? (
                                                    <button className="jv-btn-icon" onClick={() => this.handleDeactivate(vendor)} title="Deactivate">
                                                        <i className="fa-sharp fa-regular fa-ban"></i>
                                                    </button>
                                                ) : (
                                                    <button className="jv-btn-icon" onClick={() => this.handleReactivate(vendor)} title="Reactivate">
                                                        <i className="fa-sharp fa-regular fa-rotate-right"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {showAddModal && (
                    <AddVendorModal
                        show={showAddModal}
                        onHide={() => this.setState({ showAddModal: false })}
                        installerId={this.props.typeId}
                        onSaved={this.loadVendors}
                    />
                )}
            </div>
        );
    }
}

export default VendorList;
